import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { adminAPI } from "@/lib/admin-api";
import {
  type EventItem,
  parseEventDate,
  resolveEventImageUrl,
  formatEventDateRange,
  getEventStatusRange,
} from "@/lib/events";
import {
  ArrowRight,
  Building2,
  Calendar,
  FileText,
  Image as ImageIcon,
  MapPin,
  Plus,
  RefreshCw,
  Settings,
  Star,
  Users,
} from "lucide-react";

type LoadState = "loading" | "ready" | "error";

function greetingForHour(hour: number) {
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function greetingName(user: { full_name?: string; username?: string } | null) {
  const full = user?.full_name?.trim();
  if (full && !/^system(\s+administrator)?$/i.test(full) && !/^admin$/i.test(full)) {
    return full.split(/\s+/)[0];
  }
  const username = user?.username?.trim();
  if (username && !/^admin$/i.test(username)) return username;
  return null;
}

function sortUpcoming(events: EventItem[]) {
  return events
    .filter((event) => getEventStatusRange(event) === "upcoming")
    .sort((a, b) => {
      const aTime = parseEventDate(a.event_date_from ?? a.event_date)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      const bTime = parseEventDate(b.event_date_from ?? b.event_date)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      return aTime - bTime;
    });
}

function sortRecent(events: EventItem[]) {
  return [...events].sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
}

export function AdminDashboard() {
  const user = adminAPI.getCurrentUser() as {
    full_name?: string;
    username?: string;
    role?: string;
  } | null;

  const [state, setState] = useState<LoadState>("loading");
  const [events, setEvents] = useState<EventItem[]>([]);
  const [speakerCount, setSpeakerCount] = useState(0);
  const [agendaCount, setAgendaCount] = useState(0);
  const [sponsorCount, setSponsorCount] = useState(0);

  const loadDashboard = async () => {
    setState("loading");
    try {
      const [eventsRes, speakersRes, agendaRes, sponsorsRes] = await Promise.all([
        api.get<EventItem[]>("/api/events"),
        api.get<unknown[]>("/api/speakers"),
        api.get<unknown[]>("/api/agenda"),
        api.get<unknown[]>("/api/sponsors"),
      ]);

      setEvents(eventsRes.success && eventsRes.data ? eventsRes.data : []);
      setSpeakerCount(speakersRes.success && speakersRes.data ? speakersRes.data.length : 0);
      setAgendaCount(agendaRes.success && agendaRes.data ? agendaRes.data.length : 0);
      setSponsorCount(sponsorsRes.success && sponsorsRes.data ? sponsorsRes.data.length : 0);
      setState("ready");
    } catch {
      setState("error");
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const name = greetingName(user);
  const upcoming = useMemo(() => sortUpcoming(events), [events]);
  const recent = useMemo(() => sortRecent(events).slice(0, 5), [events]);
  const featuredCount = events.filter((event) => Boolean(event.is_featured)).length;
  const pastCount = events.filter((event) => getEventStatusRange(event) === "past").length;

  const nextSteps = [
    events.length === 0 && {
      title: "Create your first event",
      detail: "The homepage hero and event list stay empty until you add one.",
      href: "/admin/event",
      label: "Add event",
    },
    speakerCount === 0 && {
      title: "Add speakers",
      detail: "Build the speaker catalog so you can attach people to each event.",
      href: "/admin/speakers",
      label: "Add speakers",
    },
    sponsorCount === 0 && {
      title: "Add sponsors",
      detail: "Sponsors appear on the public site and can be linked per event.",
      href: "/admin/sponsors",
      label: "Add sponsors",
    },
    agendaCount === 0 && {
      title: "Publish an agenda",
      detail: "Sessions help visitors plan their day and fill the agenda page.",
      href: "/admin/agenda",
      label: "Add agenda",
    },
  ].filter(Boolean) as Array<{ title: string; detail: string; href: string; label: string }>;

  const stats = [
    {
      label: "Events",
      value: events.length,
      hint: featuredCount > 0 ? `${featuredCount} featured` : "All conferences",
      href: "/admin/event",
      icon: FileText,
    },
    {
      label: "Upcoming",
      value: upcoming.length,
      hint: pastCount > 0 ? `${pastCount} already held` : "Nothing scheduled yet",
      href: "/admin/event",
      icon: Calendar,
    },
    {
      label: "Speakers",
      value: speakerCount,
      hint: "Ready to assign",
      href: "/admin/speakers",
      icon: Users,
    },
    {
      label: "Sponsors",
      value: sponsorCount,
      hint: `${agendaCount} agenda items`,
      href: "/admin/sponsors",
      icon: Building2,
    },
  ];

  const tasks = [
    {
      title: "Create an event",
      detail: "Title, date, venue, speakers, and registration link in one place.",
      href: "/admin/event",
      icon: Plus,
    },
    {
      title: "Update speakers",
      detail: "Keep names, roles, and photos current for the public speaker page.",
      href: "/admin/speakers",
      icon: Users,
    },
    {
      title: "Edit About",
      detail: "Refresh the story, stats, and images visitors see first.",
      href: "/admin/about",
      icon: Settings,
    },
    {
      title: "Upload media",
      detail: "Add photos for events, galleries, and speaker portraits.",
      href: "/admin/media",
      icon: ImageIcon,
    },
  ];

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{format(new Date(), "EEEE, d MMMM yyyy")}</p>
          <h1 className="mt-1 font-serif text-3xl text-primary">
            {name ? `${greetingForHour(new Date().getHours())}, ${name}` : greetingForHour(new Date().getHours())}
          </h1>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">
            Start with the next event, then keep speakers, agenda, and sponsors in sync with the
            public site.
          </p>
        </div>
        <Button asChild>
          <Link to="/admin/event">
            <Plus className="h-4 w-4" />
            New event
          </Link>
        </Button>
      </div>

      {state === "error" && (
        <Card className="border-destructive/30 bg-destructive/5 p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-medium text-foreground">Could not load dashboard data</p>
              <p className="text-sm text-muted-foreground">
                Check that the API is running, then try again.
              </p>
            </div>
            <Button variant="outline" onClick={loadDashboard}>
              <RefreshCw className="h-4 w-4" />
              Retry
            </Button>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {state === "loading"
          ? Array.from({ length: 4 }).map((_, index) => (
              <Card key={index} className="p-5">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="mt-3 h-8 w-12" />
                <Skeleton className="mt-3 h-3 w-28" />
              </Card>
            ))
          : stats.map((stat) => (
              <Link key={stat.label} to={stat.href} className="block">
                <Card className="h-full p-5 transition-colors hover:border-primary/30 hover:bg-card">
                  <div className="flex items-start justify-between">
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                    <stat.icon className="h-4 w-4 text-accent" />
                  </div>
                  <p className="mt-2 font-serif text-3xl text-primary">{stat.value}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{stat.hint}</p>
                </Card>
              </Link>
            ))}
      </div>

      {state === "ready" && nextSteps.length > 0 && (
        <Card className="p-5 sm:p-6">
          <div className="mb-4">
            <h2 className="font-serif text-xl text-primary">Suggested next steps</h2>
            <p className="text-sm text-muted-foreground">
              Finish these so the public site has something to show.
            </p>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {nextSteps.map((step) => (
              <div
                key={step.title}
                className="flex items-start justify-between gap-4 rounded-lg border bg-muted/40 p-4"
              >
                <div>
                  <p className="font-medium text-foreground">{step.title}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{step.detail}</p>
                </div>
                <Button size="sm" asChild>
                  <Link to={step.href}>{step.label}</Link>
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
        <Card className="p-5 sm:p-6 xl:col-span-3">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h2 className="font-serif text-xl text-primary">Upcoming events</h2>
              <p className="text-sm text-muted-foreground">What visitors will see next</p>
            </div>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/admin/event">
                Manage
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>

          {state === "loading" ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <Skeleton key={index} className="h-20 w-full" />
              ))}
            </div>
          ) : upcoming.length === 0 ? (
            <div className="rounded-lg border border-dashed bg-muted/30 px-4 py-10 text-center">
              <Calendar className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
              <p className="font-medium text-foreground">No upcoming events</p>
              <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
                Add a dated event and it will appear here and on the homepage slider.
              </p>
              <Button className="mt-4" asChild>
                <Link to="/admin/event">Create event</Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {upcoming.slice(0, 4).map((event) => (
                <Link
                  key={event.id}
                  to="/admin/event"
                  className="flex items-center gap-4 rounded-lg border p-3 transition-colors hover:bg-muted/40"
                >
                  <img
                    src={resolveEventImageUrl(event.image_url)}
                    alt=""
                    className="h-14 w-16 shrink-0 rounded-md object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate font-medium text-foreground">{event.title}</p>
                      {Boolean(event.is_featured) && (
                        <Badge variant="secondary" className="gap-1">
                          <Star className="h-3 w-3" />
                          Featured
                        </Badge>
                      )}
                    </div>
                    <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <span>{formatEventDateRange(event) || "Date to be confirmed"}</span>
                      {event.location && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {event.location}
                        </span>
                      )}
                    </p>
                  </div>
                  <ArrowRight className="hidden h-4 w-4 shrink-0 text-muted-foreground sm:block" />
                </Link>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5 sm:p-6 xl:col-span-2">
          <div className="mb-4">
            <h2 className="font-serif text-xl text-primary">Recently added</h2>
            <p className="text-sm text-muted-foreground">Newest events first</p>
          </div>
          {state === "loading" ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={index} className="h-12 w-full" />
              ))}
            </div>
          ) : recent.length === 0 ? (
            <p className="text-sm text-muted-foreground">No events yet.</p>
          ) : (
            <ul className="space-y-3">
              {recent.map((event) => {
                const status = getEventStatusRange(event);
                return (
                  <li key={event.id} className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">{event.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatEventDateRange(event) || "No date"}
                      </p>
                    </div>
                    <Badge variant={status === "upcoming" ? "default" : "secondary"}>
                      {status === "unknown" ? "Undated" : status}
                    </Badge>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>

      <div>
        <h2 className="mb-3 font-serif text-xl text-primary">Everyday tasks</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {tasks.map((task) => (
            <Link key={task.title} to={task.href} className="block">
              <Card className="h-full p-5 transition-colors hover:border-primary/30">
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent/15 text-accent-foreground">
                    <task.icon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground">{task.title}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{task.detail}</p>
                  </div>
                  <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
